<template>
  <li ref="menuLink" class="mr-12 flex items-center text-sm">
    <div>
      <div
        class="flex items-center"
        :class="[
          menuItemTextClass,
          { 'opacity-50': !dark && isMenuOpen && !isActive },
          { 'opacity-100': isMenuOpen && isActive },
        ]"
      >
        <UiLink :to="menuItem.url" class="font-bold uppercase tracking-[.12em]">
          {{ menuItem.name }}
        </UiLink>
        <UiIcon
          v-if="hasChildren"
          class="ml-2 h-3 w-3"
          :icon="isActive ? 'chevronUp' : 'chevronDown'"
        />
      </div>

      <transition
        enter-active-class="transform duration-100"
        enter-from-class="opacity-0 -translate-y-4"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transform duration-100"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-4"
      >
        <div
          v-if="hasChildren"
          v-show="isActive"
          class="absolute left-0 right-0 top-full bg-gray-lighten3 shadow-innerMenu"
        >
          <div class="container relative flex gap-10">
            <ul
              class="flex w-full min-w-[267px] max-w-fit flex-col py-8 pr-6 text-base font-bold uppercase text-slate shadow-menu"
            >
              <li
                v-for="category in menuItem.children"
                :key="category.name"
                class="cursor-pointer rounded-sm transition-all duration-300"
                :class="{
                  'bg-blue text-white':
                    hasImages && activeCategoryId === category.id,
                }"
                @mouseover="setActiveCategory(category)"
              >
                <UiLink
                  :to="category.url"
                  class="group flex items-center px-2 py-6 font-bold uppercase tracking-[.12em]"
                >
                  {{ category.name }}
                </UiLink>
              </li>
            </ul>

            <div class="h-full w-full self-center py-8">
              <div v-if="activeCategoryChildren">
                <h4 class="text-base font-bold uppercase text-blue">
                  {{ activeCategoryName }}
                </h4>

                <div class="mb-6 mt-3 h-[1px] w-full bg-gray-lighten1"></div>
              </div>
              <div class="flex h-full w-full">
                <ul
                  class="grid h-full w-full grid-cols-3 flex-wrap gap-x-12 gap-y-4 text-sm uppercase md:max-w-[90%] lg:max-w-[70%]"
                >
                  <template v-if="activeCategoryChildren">
                    <li
                      v-for="(child, index) in activeCategoryChildren"
                      :key="index"
                      class="flex flex-col gap-y-3 text-sm"
                    >
                      <UiLink :to="child.url" class="font-bold text-slate">
                        {{ child.name }}
                      </UiLink>
                      <div
                        v-if="child.children && child.children.length"
                        class="flex flex-col gap-y-2"
                      >
                        <UiLink
                          v-for="item in child.children"
                          :key="item.id"
                          :to="item.url"
                          class="font-normal hover:underline hover:decoration-grey-light hover:decoration-solid"
                        >
                          {{ item.name }}
                        </UiLink>
                      </div>
                    </li>
                  </template>
                </ul>
                <div
                  v-if="hasImages"
                  class="h-[255px] w-full max-w-[291px] self-center rounded-md bg-cover bg-center md:hidden lg:block"
                  :style="{ 'background-image': activeCategoryImage }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </li>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useElementHover } from '@vueuse/core';

import { useMediaAsset } from '~/composables';

interface Props {
  menuItem?: any;
  isActive?: boolean;
  isMenuOpen?: boolean;
  dark?: boolean;
}
interface Emits {
  (e: 'setActiveItem'): void;
}

const props = withDefaults(defineProps<Props>(), {
  menuItem: () => [],
  isActive: false,
  isMenuOpen: false,
  dark: false,
});
const emit = defineEmits<Emits>();

const { joinuApp: isJoinuApp } = useRuntimeConfig().public.frontkit;
const menuLink = ref();
const isHoveredLink = useElementHover(menuLink);
const { getAssetUrl } = useMediaAsset();
const activeCategoryId = ref(null);
const activeCategoryName = ref(null);
const activeCategoryImage = ref<string>();
const activeCategoryChildren = ref<null | any[]>(null);

const hasChildren = computed(() => Boolean(props.menuItem.children?.length));

const hasImages = computed(
  () =>
    hasChildren.value &&
    props.menuItem.children.some((item: any) => item.image),
);

const setActiveCategory = (category: any) => {
  activeCategoryId.value = category?.id ?? null;

  activeCategoryName.value = category?.name ?? null;
  activeCategoryImage.value = category?.image?.path
    ? `url('${getAssetUrl(category.image.path)}')`
    : '';
  activeCategoryChildren.value =
    category?.children && category?.children.length ? category?.children : null;
};
const menuItemTextClass = computed(() =>
  props.dark || props.isMenuOpen || isJoinuApp ? 'text-slate' : 'text-white',
);

watch(isHoveredLink, (value) => {
  emit('setActiveItem');
  if (!value) setActiveCategory(null);
});
</script>
